import { State as CaseVoucherState } from '@debbie/common/dist/economy/case-vouchers/index.js';
import { VoucherTypeSetting } from '@debbie/common/dist/economy/vouchers/index.js';
import { formatNumber } from '@debbie/common/dist/index.js';
import { CaseVoucherAmountMode } from '@debbie/common/dist/insights/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import * as caseFilterStore from '@debbie/cortex/dist/insights/caseFilter.js';
import { useVoucherTypes } from '@debbie/cortex/dist/voucher-types/index.js';
import React, { useEffect, useId, useState } from 'react';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import { styled } from 'styled-components';
import THEME from '../../../theme.js';
import Button from '../../Button.js';
import ButtonGroup from '../../ButtonGroup.js';
import CaseVoucherTypeSelect from '../../CaseVoucherTypeSelect.js';
import CurrencySelect from '../../CurrencySelect.js';
import MoneyInput from '../../MoneyInput.js';
import PaymentTypeSelect from '../../PaymentTypeSelect.js';
import FilterGroup from '../FilterGroup.js';
import FilterOption from '../FilterOption.js';
const EconomyGroup = ({ caseFilter }) => {
    const lang = useSelector((s) => s.lang);
    const text = lang.caseFilter.economyFilter;
    const voucherTypesLoadable = useVoucherTypes();
    const voucherTypes = h.hasData(voucherTypesLoadable)
        ? voucherTypesLoadable.data
        : [];
    const includesPayment = (ids) => voucherTypes.some((type) => ids.includes(type.voucherTypeId) &&
        type.setting === VoucherTypeSetting.Payment);
    const showPaymentFilters = includesPayment(caseFilter.economyOnlyVoucherTypeIds ?? []) ||
        caseFilter.economyVoucherPaymentType != null ||
        (caseFilter.economyVoucherStates?.length ?? 0) > 0;
    const total = caseFilter.economyVoucherAmountMode === CaseVoucherAmountMode.Total;
    const statesId = useId();
    const hasPostings = (caseFilter.economyOnlyVoucherTypeIds?.length ?? 0) > 0 ||
        caseFilter.economyMinVoucherAmount != null ||
        caseFilter.economyMaxVoucherAmount != null ||
        caseFilter.economyVoucherAmountMode != null ||
        caseFilter.economyVoucherPaymentType != null ||
        (caseFilter.economyVoucherStates?.length ?? 0) > 0;
    const hasDebt = caseFilter.economyMinBalance != null || caseFilter.economyMaxBalance != null;
    return (React.createElement(FilterGroup, { name: text.postingsGroup },
        React.createElement(Intro, null, text.combinedHint),
        React.createElement(FilterOption, { active: caseFilter.caseCurrency != null, header: lang.mentionContent.currency, onDeactivate: () => caseFilterStore.clearFilter('caseCurrency') },
            React.createElement(CurrencySelect, { value: caseFilter.caseCurrency, onChange: (caseCurrency) => caseFilterStore.updateFilter({ caseCurrency }) })),
        React.createElement(FilterOption, { active: hasPostings, header: text.postings, subheader: text.postingsHint, onDeactivate: () => caseFilterStore.updateFilter({
                economyOnlyVoucherTypeIds: undefined,
                economyVoucherAmountMode: undefined,
                economyVoucherPaymentType: undefined,
                economyVoucherStates: undefined,
                economyMinVoucherAmount: undefined,
                economyMaxVoucherAmount: undefined,
            }) },
            React.createElement(CaseVoucherTypeSelect, { multi: true, value: caseFilter.economyOnlyVoucherTypeIds ?? [], placeholder: text.anyType, onChange: (ids) => caseFilterStore.updateFilter({
                    economyOnlyVoucherTypeIds: ids.length > 0 ? ids : undefined,
                    ...(!includesPayment(ids)
                        ? {
                            economyVoucherPaymentType: undefined,
                            economyVoucherStates: undefined,
                        }
                        : {}),
                }) }),
            React.createElement(Hint, null, text.typesHint),
            showPaymentFilters && (React.createElement(PaymentFilters, null,
                React.createElement("div", null,
                    React.createElement(FieldTitle, null, text.paymentType),
                    React.createElement(PaymentTypeSelect, { value: caseFilter.economyVoucherPaymentType ?? null, placeholder: text.allPaymentTypes, onChange: (_id, paymentType) => caseFilterStore.updateFilter({
                            economyVoucherPaymentType: paymentType ?? undefined,
                        }) })),
                React.createElement("div", null,
                    React.createElement(FieldTitle, { as: "label", htmlFor: statesId }, text.voucherState),
                    React.createElement(Select, { inputId: statesId, isMulti: true, classNamePrefix: "select", placeholder: text.allVoucherStates, value: (caseFilter.economyVoucherStates ?? []).map((state) => ({
                            value: state,
                            label: lang.caseVoucherStates[state],
                        })), options: Object.values(CaseVoucherState).map((state) => ({
                            value: state,
                            label: lang.caseVoucherStates[state],
                        })), onChange: (options) => caseFilterStore.updateFilter({
                            economyVoucherStates: options.length > 0
                                ? options.map((option) => option.value)
                                : undefined,
                        }) })),
                React.createElement(Hint, null, text.paymentHint))),
            React.createElement(ModeOptions, { role: "group", "aria-label": text.amountMode },
                React.createElement(FieldTitle, null, text.amountMode),
                React.createElement(ButtonGroup, { blockOnMobile: true },
                    React.createElement(Button, { type: "button", size: "mini", theme: total ? 'secondary' : undefined, pressed: !total, onClick: () => caseFilterStore.clearFilter('economyVoucherAmountMode') }, text.individualAmount),
                    React.createElement(Button, { type: "button", size: "mini", theme: total ? undefined : 'secondary', pressed: total, onClick: () => caseFilterStore.updateFilter({
                            economyVoucherAmountMode: CaseVoucherAmountMode.Total,
                        }) }, text.totalAmount)),
                React.createElement(Hint, null, total ? text.totalExample : text.individualExample),
                total && React.createElement(Hint, null, text.totalHint)),
            React.createElement(AmountRange, { key: JSON.stringify([
                    caseFilter.economyOnlyVoucherTypeIds,
                    caseFilter.economyVoucherAmountMode,
                    caseFilter.economyVoucherPaymentType,
                    caseFilter.economyVoucherStates,
                ]), title: total ? text.totalAmountLabel : text.amount, min: 0, minimum: caseFilter.economyMinVoucherAmount, maximum: caseFilter.economyMaxVoucherAmount, onChange: (minimum, maximum) => caseFilterStore.updateFilter({
                    economyMinVoucherAmount: minimum,
                    economyMaxVoucherAmount: maximum,
                }) }),
            React.createElement(Explanation, null,
                React.createElement("strong", null, text.amountHint))),
        React.createElement(FilterOption, { active: hasDebt, header: text.debt, subheader: text.debtHint, onDeactivate: () => caseFilterStore.updateFilter({
                economyMinBalance: undefined,
                economyMaxBalance: undefined,
            }) },
            React.createElement(AmountRange, { title: text.debt, hideTitle: true, minimum: caseFilter.economyMinBalance, maximum: caseFilter.economyMaxBalance, onChange: (minimum, maximum) => caseFilterStore.updateFilter({
                    economyMinBalance: minimum,
                    economyMaxBalance: maximum,
                }) }),
            React.createElement(Hint, null, text.debtNote))));
};
const AmountRange = ({ title, hideTitle = false, min, minimum, maximum, onChange, }) => {
    const lang = useSelector((s) => s.lang);
    const text = lang.caseFilter.economyFilter;
    const id = useId();
    const [from, setFrom] = useState(minimum ?? null);
    const [to, setTo] = useState(maximum ?? null);
    useEffect(() => {
        setFrom(minimum ?? null);
        setTo(maximum ?? null);
    }, [minimum, maximum]);
    const invalid = from != null && to != null && from > to;
    const commit = () => {
        if (!invalid && (from !== (minimum ?? null) || to !== (maximum ?? null))) {
            onChange(from ?? undefined, to ?? undefined);
        }
    };
    useEffect(() => {
        if (invalid || (from === (minimum ?? null) && to === (maximum ?? null))) {
            return undefined;
        }
        const timeout = setTimeout(() => onChange(from ?? undefined, to ?? undefined), 500);
        return () => clearTimeout(timeout);
    }, [from, to, minimum, maximum, invalid, onChange]);
    const summary = from != null && to != null
        ? `${formatNumber(from)} – ${formatNumber(to)}`
        : from != null
            ? `≥ ${formatNumber(from)}`
            : to != null
                ? `≤ ${formatNumber(to)}`
                : text.anyAmount;
    return (React.createElement(Range, null,
        React.createElement(RangeHeading, null,
            !hideTitle && React.createElement(FieldTitle, null, title),
            React.createElement(RangeSummary, { "aria-live": "polite" }, invalid ? '—' : summary)),
        React.createElement(RangeFields, null,
            React.createElement("div", null,
                React.createElement("label", { htmlFor: `${id}-from` }, text.minimum),
                React.createElement(MoneyInput, { id: `${id}-from`, value: from, allowNull: true, min: min, placeholder: text.noLimit, aria: {
                        'aria-invalid': invalid,
                        'aria-describedby': `${id}-hint`,
                        'aria-label': `${title}: ${text.minimum}`,
                    }, onChange: setFrom, onBlur: commit, onKeyDown: (event) => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                        }
                        if (event.key === 'Enter' || event.key === 'Escape') {
                            commit();
                        }
                    } })),
            React.createElement(RangeDash, { "aria-hidden": "true" }, "\u2013"),
            React.createElement("div", null,
                React.createElement("label", { htmlFor: `${id}-to` }, text.maximum),
                React.createElement(MoneyInput, { id: `${id}-to`, value: to, allowNull: true, min: min, placeholder: text.noLimit, aria: {
                        'aria-invalid': invalid,
                        'aria-describedby': `${id}-hint`,
                        'aria-label': `${title}: ${text.maximum}`,
                    }, onChange: setTo, onBlur: commit, onKeyDown: (event) => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                        }
                        if (event.key === 'Enter' || event.key === 'Escape') {
                            commit();
                        }
                    } }))),
        React.createElement(RangeHint, { id: `${id}-hint`, $invalid: invalid, role: invalid ? 'alert' : undefined }, invalid ? text.rangeError : text.currencyHint)));
};
export default EconomyGroup;
const Hint = styled.p `
  margin: 5px 0;
  font-size: 0.9em;
  line-height: 1.5;
  color: #666;
`;
const Intro = styled(Hint) `
  margin: 10px 0;
`;
const FieldTitle = styled.h4 `
  margin: 0 0 0.4em;
`;
const Range = styled.div `
  margin-top: 15px;
`;
const RangeHeading = styled.div `
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;
const RangeSummary = styled.span `
  margin-left: auto;
  color: #666;
  font-size: 0.85em;
  font-variant-numeric: tabular-nums;
`;
const RangeFields = styled.div `
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  gap: 10px;
  align-items: end;
  label {
    display: block;
    font-size: 0.9em;
    margin-bottom: 6px;
  }
  .iris-input-container label {
    margin: 0;
    font-size: inherit;
  }
  input {
    margin-bottom: 5px;
    padding: 12px 15px;
  }
  .inp-placeholder.active {
    display: none;
  }
  @media (max-width: 480px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const RangeDash = styled.span `
  padding-bottom: 17px;
  color: #666;
  @media (max-width: 480px) {
    display: none;
  }
`;
const RangeHint = styled(Hint) `
  color: ${(p) => (p.$invalid ? THEME.DANGEROUS : '#666')};
`;
const Explanation = styled(Hint) `
  margin: 10px 0;
  strong,
  span {
    display: block;
  }
  span {
    margin-top: 5px;
  }
`;
const ModeOptions = styled.div `
  margin-top: 15px;
`;
const PaymentFilters = styled.div `
  margin-top: 15px;
  display: grid;
  gap: 10px;
`;
//# sourceMappingURL=EconomyGroup.js.map